/**
 * Redux Store Configuration
 * 
 * This file configures the main Redux store for the application,
 * combining all slice reducers and setting up middleware.
 * The store manages global application state including authentication,
 * self-appraisal data, nominations, and navigation state.
 */

import { configureStore } from '@reduxjs/toolkit';
import authReducer from './authSlice';
import selfAppraisalReducer from './selfAppraisalSlice';
import nominationReducer from './nominationSlice';
import navigationReducer from './navigationSlice';

/**
 * Configure and create the Redux store
 * 
 * Combines all slice reducers into a single store and configures
 * middleware for enhanced development experience and state persistence.
 */
export const store = configureStore({
  reducer: {
    // Authentication state (user info, login status)
    auth: authReducer,
    // Self-appraisal form data and status
    selfAppraisal: selfAppraisalReducer,
    // Nomination data and reviews
    nomination: nominationReducer,
    // Navigation and UI state
    navigation: navigationReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Configure serializable check to ignore persist actions
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }),
});

// Type definitions for TypeScript integration
// These types enable type-safe access to the store state and dispatch
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
